import type { FC } from 'react';
import PropTypes from 'prop-types';
import { Box, Drawer, Stack, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Scrollbar } from 'src/components/scrollbar';
import { usePathname } from 'src/hooks/use-pathname';
import type { NavColor } from 'src/api/types/settings';
import type { Section } from '../config';
import { SideNavSection } from './side-nav-section';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const SIDE_NAV_WIDTH: number = 200;
const COLLAPSED_SIDE_NAV_WIDTH: number = 64;

const useCssVars = (color: NavColor): Record<string, string> => {
  const theme = useTheme();

  switch (color) {
    case 'blend-in':
      if (theme.palette.mode === 'dark') {
        return {
          '--nav-bg': theme.palette.background.default,
          '--nav-color': theme.palette.neutral[100],
          '--nav-border-color': theme.palette.neutral[700],
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[600],
          '--nav-item-icon-color': theme.palette.neutral[500],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[700],
          '--nav-item-chevron-color': theme.palette.neutral[700],
          '--nav-scrollbar-color': theme.palette.neutral[400]
        };
      } else {
        return {
          '--nav-bg': theme.palette.background.default,
          '--nav-color': theme.palette.text.primary,
          '--nav-border-color': theme.palette.divider,
          '--nav-logo-border': theme.palette.neutral[200],
          '--nav-section-title-color': theme.palette.neutral[500],
          '--nav-item-color': theme.palette.neutral[500],
          '--nav-item-hover-bg': theme.palette.action.hover,
          '--nav-item-active-bg': theme.palette.action.selected,
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[400],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[400],
          '--nav-item-chevron-color': theme.palette.neutral[400],
          '--nav-scrollbar-color': theme.palette.neutral[900]
        };
      }

    case 'discreet':
      if (theme.palette.mode === 'dark') {
        return {
          '--nav-bg': theme.palette.neutral[900],
          '--nav-color': theme.palette.neutral[100],
          '--nav-border-color': theme.palette.neutral[700],
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[600],
          '--nav-item-icon-color': theme.palette.neutral[500],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[700],
          '--nav-item-chevron-color': theme.palette.neutral[700],
          '--nav-scrollbar-color': theme.palette.neutral[400]
        };
      } else {
        return {
          '--nav-bg': theme.palette.neutral[50],
          '--nav-color': theme.palette.text.primary,
          '--nav-border-color': theme.palette.divider,
          '--nav-logo-border': theme.palette.neutral[200],
          '--nav-section-title-color': theme.palette.neutral[500],
          '--nav-item-color': theme.palette.neutral[500],
          '--nav-item-hover-bg': theme.palette.action.hover,
          '--nav-item-active-bg': theme.palette.action.selected,
          '--nav-item-active-color': theme.palette.text.primary,
          '--nav-item-disabled-color': theme.palette.neutral[400],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[400],
          '--nav-item-chevron-color': theme.palette.neutral[400],
          '--nav-scrollbar-color': theme.palette.neutral[900]
        };
      }

    case 'evident':
      if (theme.palette.mode === 'dark') {
        return {
          '--nav-bg': theme.palette.neutral[800],
          '--nav-color': theme.palette.common.white,
          '--nav-border-color': 'transparent',
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': theme.palette.common.white,
          '--nav-item-disabled-color': theme.palette.neutral[500],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[500],
          '--nav-item-chevron-color': theme.palette.neutral[600],
          '--nav-scrollbar-color': theme.palette.neutral[400]
        };
      } else {
        return {
          '--nav-bg': theme.palette.neutral[800],
          '--nav-color': theme.palette.common.white,
          '--nav-border-color': 'transparent',
          '--nav-logo-border': theme.palette.neutral[700],
          '--nav-section-title-color': theme.palette.neutral[400],
          '--nav-item-color': theme.palette.neutral[400],
          '--nav-item-hover-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-bg': 'rgba(255, 255, 255, 0.04)',
          '--nav-item-active-color': theme.palette.common.white,
          '--nav-item-disabled-color': theme.palette.neutral[500],
          '--nav-item-icon-color': theme.palette.neutral[400],
          '--nav-item-icon-active-color': theme.palette.primary.main,
          '--nav-item-icon-disabled-color': theme.palette.neutral[500],
          '--nav-item-chevron-color': theme.palette.neutral[600],
          '--nav-scrollbar-color': theme.palette.neutral[400]
        };
      }

    default:
      return {};
  }
};

interface SideNavProps {
  color?: NavColor;
  sections?: Section[];
  onCollapse?: () => void;
  isCollapsed?: boolean;
}

export const SideNav: FC<SideNavProps> = (props) => {
  const { color = 'evident', sections = [], onCollapse, isCollapsed = false } = props;
  const pathname = usePathname();
  const cssVars = useCssVars(color);

  return (
    <Drawer
      anchor="left"
      open
      PaperProps={{
        sx: {
          ...cssVars,
          backgroundColor: 'var(--nav-bg)',
          borderRightColor: 'var(--nav-border-color)',
          borderRightStyle: 'solid',
          borderRightWidth: 1,
          color: 'var(--nav-color)',
          width: isCollapsed ? COLLAPSED_SIDE_NAV_WIDTH : SIDE_NAV_WIDTH,
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden'
        }
      }}
      variant="permanent"
    >
      <Scrollbar
        sx={{
          height: '100%',
          '& .simplebar-content': {
            height: '100%'
          },
          '& .simplebar-scrollbar:before': {
            background: 'var(--nav-scrollbar-color)'
          }
        }}
      >
        <Stack sx={{ height: '100%' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={isCollapsed ? 'center' : 'flex-start'}
            sx={{ p: 2 }}
          >
            <Box
              component="img"
              src="/assets/iconly/logo-transparent.png"
              alt="Logo"
              sx={{
                height: '65%',
                width: isCollapsed ? '80%' : '80%',
                display: 'flex',
                objectFit: 'contain',
                transition: 'all 0.2s ease-in-out'
              }}
            />
          </Stack>
          <Stack
            component="nav"
            spacing={2}
            sx={{
              flexGrow: 1,
              px: isCollapsed ? 0.5 : 2,
              transition: 'padding 0.2s ease-in-out'
            }}
          >
            {sections.map((section, index) => (
              <SideNavSection
                items={section.items}
                key={index}
                pathname={pathname}
                subheader={isCollapsed ? '' : section.subheader}
                isCollapsed={isCollapsed}
              />
            ))}
          </Stack>
          <Box
            sx={{
              p: 1,
              display: 'flex',
              justifyContent: isCollapsed ? 'center' : 'flex-end',
              borderTop: 1,
              borderColor: 'var(--nav-border-color)'
            }}
          >
            <Tooltip title={isCollapsed ? "Expand" : "Collapse"} placement="right">
              <IconButton
                onClick={onCollapse}
                sx={{
                  color: 'var(--nav-item-color)',
                  '&:hover': {
                    backgroundColor: 'var(--nav-item-hover-bg)'
                  }
                }}
              >
                {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
};

SideNav.propTypes = {
  color: PropTypes.oneOf(['blend-in', 'discreet', 'evident']),
  sections: PropTypes.array,
  onCollapse: PropTypes.func,
  isCollapsed: PropTypes.bool
};
